'use client'
import { Skeleton } from '@mantine/core'
import StarRating from '@/components/starraing-new'
import Image from 'next/image'
import RatingShowsSkeleton from './RatingShowsSkeleton'
import RatingsBarNew from '@/components/rating-bar-new'
import PropTypes from 'prop-types'
import { useTranslations } from 'next-intl'
import ReviewSection from './ReviewSection'

const reviewsBarData = {
  '5_rating': 0,
  '4_rating': 0,
  '3_rating': 0,
  '2_rating': 0,
  '1_rating': 0,
}

const ReviewsMobile = ({
  loadingReviews,
  reviewsDataNew,
  reviewCategoriesMobile,
  platformDataMobile,
  skeletonData,
  totalCount,
  id,
}) => {
  const t = useTranslations('propertydetails')
  const hasNoReviews = !loadingReviews && (reviewsDataNew?.data?.review?.length ?? 0) === 0

  const SkeletonRatingsBar = () => {
    return (
      <div className='w-[385px] space-y-[5.03px]'>
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className='flex items-center gap-2'>
            {/* Star number skeleton */}
            <Skeleton height={15} width={12} className='rounded' />

            {/* Progress bar skeleton */}
            <div className='flex-1'>
              <Skeleton height={12} className='rounded-full' />
            </div>

            {/* Count skeleton */}
            <Skeleton height={12} width={12} className='rounded' />
          </div>
        ))}
      </div>
    )
  }
  return (
    <>
      <section className='pt-5 md:hidden mb-4 w-full overflow-hidden md:mx-auto'>
        {loadingReviews ? (
          <Skeleton visible={loadingReviews} height={10} width={100} />
        ) : (
          <p className='font-semibold text-[18px] leading-[150%] tracking-[0] text-[#1E1F24] mt-2 mb-6 mx-[8px]'>
            {hasNoReviews
              ? t('reviews.emptyState.sectionTitle')
              : t('reviews.sectionTitle')}
          </p>
        )}

        {hasNoReviews && (
          <div className='flex flex-col gap-10 items-start mx-[8px]'>
            <div className='flex flex-col gap-3 mt-2'>
              <div className='bg-white shadow-md rounded-2xl p-6 w-full max-w-sm flex flex-col items-start gap-3 border border-gray-100'>
                <span className='inline-flex items-center justify-center text-[#2CA81E] bg-[#ECFAF0] text-[11px] font-semibold rounded-[30px] px-2 py-0.5'>
                  {t('badges.new')}
                </span>
                <h2 className='font-semibold text-[18px] leading-[150%] tracking-[0] text-[#1E1F24]'>
                  {t('reviews.emptyState.title')}
                </h2>
                <p className='text-gray-500 text-sm text-start'>
                  {t('reviews.emptyState.description')}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className='flex flex-col gap-10 items-start mx-[8px]'>
          <div className='flex flex-col gap-3 mt-2'>
            {loadingReviews ? (
              <>
                <Skeleton height={87} width={99} radius='sm' />
                <Skeleton height={25} width={150} radius='sm' />
                <Skeleton height={26} width={129} radius='sm' />
              </>
            ) : (
              reviewsDataNew?.data?.review?.length !== 0 && (
                <>
                  <span className='font-semibold text-[62px] leading-[140%] tracking-[1%] align-middle text-[#319341] opacity-100 w-[99px] h-[87px]'>
                    {parseFloat(
                      reviewsDataNew?.data?.overall_rating || 0,
                    ).toFixed(1)}
                  </span>
                  <StarRating
                    rating={parseFloat(
                      reviewsDataNew?.data?.overall_rating || 0,
                    ).toFixed(1)}
                    className='h-fit items-center'
                    IconClassName='text-[#319341] text-sm align-middle'
                    size={25}
                  />
                  <span className='font-normal text-[16px] leading-[160%] tracking-[0] text-[#1E1F24] opacity-100 w-[129px] h-[26px]'>
                    <>
                      {totalCount} {totalCount === 1 ? 'rating' : 'ratings'}
                    </>
                  </span>
                </>
              )
            )}
          </div>

          {loadingReviews ? (
            <SkeletonRatingsBar />
          ) : (
            reviewsDataNew?.data?.review?.length !== 0 && (
              <RatingsBarNew
                reviews={reviewsDataNew?.data?.reviews ?? reviewsBarData}
              />
            )
          )}
        </div>

        {loadingReviews ? (
          <div className='w-full h-full flex flex-wrap gap-[36px] my-12'>
            {/* Value for Money Skeleton */}
            <div className='w-[110px] h-[93px] gap-3'>
              <div className='flex flex-col justify-start items-start'>
                <Skeleton height={21} width={21} radius='sm' />
                <div className='mt-4'>
                  <Skeleton
                    height={16}
                    width={85}
                    className='mb-1'
                    radius='sm'
                  />
                </div>
                <Skeleton height={20} width={30} radius='sm' />
              </div>
            </div>
            {/* Check-in Experience Skeleton */}
            <div className='w-[150px] h-[93px] gap-3'>
              <div className='flex flex-col justify-start items-start'>
                <Skeleton height={21} width={21} radius='sm' />
                <div className='mt-4'>
                  <Skeleton
                    height={16}
                    width={120}
                    className='mb-1'
                    radius='sm'
                  />
                </div>
                <Skeleton height={20} width={30} radius='sm' />
              </div>
            </div>
            {/* Safety & Security Skeleton */}
            <div className='w-[110px] h-[93px] gap-3'>
              <div className='flex flex-col justify-start items-start'>
                <Skeleton height={21} width={21} radius='sm' />
                <div className='mt-4'>
                  <Skeleton
                    height={16}
                    width={95}
                    className='mb-1'
                    radius='sm'
                  />
                </div>
                <Skeleton height={20} width={30} radius='sm' />
              </div>
            </div>
            {/* Accuracy of Photos Skeleton */}
            <div className='w-[180px] h-[93px] gap-3'>
              <div className='flex flex-col justify-start items-start'>
                <Skeleton height={21} width={21} radius='sm' />
                <div className='mt-4'>
                  <Skeleton
                    height={16}
                    width={130}
                    className='mb-1'
                    radius='sm'
                  />
                </div>
                <Skeleton height={20} width={30} radius='sm' />
              </div>
            </div>
          </div>
        ) : (
          reviewsDataNew?.data?.review?.length !== 0 && (
            <div
              className={`w-full h-full flex-wrap gap-8 my-12 text-white mx-[15px] ${Object.entries(reviewsDataNew?.data?.average_per_category ?? {}).length === 0 ? 'hidden' : 'flex'}`}
            >
              {reviewCategoriesMobile.map(
                ({ key, label, icon }) =>
                  reviewsDataNew?.data?.average_per_category?.[key] && (
                    <div
                      key={key}
                      className='min-w-[120px] flex-1 max-w-[180px] h-[93px] gap-3'
                    >
                      <div className='flex flex-col items-start'>
                        <Image src={icon} alt={label} width={21} height={21} />
                        <span className='font-normal text-[13px] leading-[187%] text-[#191919] mt-4'>
                          {label}
                        </span>
                        <p className='font-medium text-[17px] leading-[160%] text-[#08090E]'>
                          {reviewsDataNew?.data?.average_per_category?.[key] ??
                            0.0}
                        </p>
                      </div>
                    </div>
                  ),
              )}
            </div>
          )
        )}

        {loadingReviews ? (
          <div className='md:w-full w-full h-[118.375px] mt-5 mx-[8px] flex gap-4 overflow-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'>
            {skeletonData.map((item) => (
              <div
                key={item.id}
                className={`min-w-[142px] h-[115px] rounded-[10px] bg-[#F5F5F5] p-4 flex-shrink-0
            ${item.hasBorder ? 'border border-[#949494]' : ''}
            ${item.hasSpecialLayout ? 'flex justify-center items-center' : ''}`}
              >
                <div className='flex flex-col items-center'>
                  <Skeleton
                    height={item.iconSize}
                    width={item.iconSize}
                    radius='sm'
                  />
                  <div className='mt-2'>
                    <Skeleton
                      height={16}
                      width={item.labelWidth}
                      className='mb-1'
                      radius='sm'
                    />
                  </div>
                  <Skeleton height={18} width={25} radius='sm' />
                </div>
              </div>
            ))}
          </div>
        ) : (
          reviewsDataNew?.data?.review?.length !== 0 && (
            <div className='w-full h-auto mt-5 flex gap-4 overflow-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'>
              {platformDataMobile
                .filter((platform) => {
                  const ratingValue = parseFloat(platform.rating) || 0
                  return ratingValue !== 0
                })
                .map(
                  ({
                    id,
                    icon,
                    alt,
                    label,
                    rating,
                    hasBorder,
                    hasSpecialLayout,
                    size,
                  }) => {
                    const ratingValue = parseFloat(rating) || 0

                    return (
                      <div
                        key={id}
                        className={`min-w-[120px] flex-1 max-w-[160px] h-[115px] rounded-lg bg-gray-100 p-4 flex-shrink-0
            ${hasBorder ? 'border border-gray-400' : ''}
            ${hasSpecialLayout ? 'flex justify-center items-center' : ''}`}
                      >
                        <div className='flex flex-col items-center w-full'>
                          <Image
                            src={icon}
                            alt={alt}
                            width={size}
                            height={size}
                            className='w-8 h-8'
                          />
                          <span className='mt-2 text-sm leading-relaxed font-normal text-gray-900'>
                            {label}
                          </span>
                          <p className='text-sm leading-relaxed font-semibold text-gray-900'>
                            {ratingValue.toFixed(1)}
                          </p>
                        </div>
                      </div>
                    )
                  },
                )}
            </div>
          )
        )}
      </section>

      {loadingReviews ? (
        <div className='flex flex-col gap-4 mb-10'>
          <div className='flex flex-wrap gap-8 my-2 mt-10 w-[354px] md:w-full'>
            {/* Create skeleton placeholders that match ReviewCard layout */}
            {Array.from({ length: 12 }).map((_, index) => (
              <div key={index} className='w-full md:w-[48%]'>
                <div className='bg-white rounded-lg p-4 shadow-lg w-[815px] min-w-[250px] max-w-full'>
                  {/* Top Row: Avatar + Name & Rating skeleton */}
                  <div className='flex gap-4 mb-3'>
                    {/* Avatar skeleton */}
                    <Skeleton
                      height={48}
                      width={48}
                      circle
                      className='flex-shrink-0'
                    />

                    {/* Name + Rating section skeleton */}
                    <div className='flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between'>
                      <div className='flex-1'>
                        {/* Name skeleton */}
                        <Skeleton height={16} width='45%' className='mb-1' />
                        {/* Date skeleton */}
                        <Skeleton height={14} width='35%' />
                      </div>

                      {/* Rating skeleton */}
                      <div className='flex items-center gap-1 mt-1 sm:mt-0'>
                        {/* Star rating skeleton */}
                        <Skeleton height={20} width={80} />
                        {/* Rating number skeleton */}
                        <Skeleton height={16} width={25} />
                      </div>
                    </div>
                  </div>

                  {/* Review Text skeleton - Below the avatar row */}
                  <div className='ml-0 space-y-2'>
                    <Skeleton height={14} width='100%' />
                    <Skeleton height={14} width='95%' />
                    <Skeleton height={14} width='75%' />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        // Render the ReviewSection component
        reviewsDataNew?.data?.review?.length !== 0 && (
          <ReviewSection
            id={id}
            initialReviews={reviewsDataNew?.data?.review ?? []}
            initialHasMore={Boolean(reviewsDataNew?.data?.next)}
            initialTotalCount={Number(reviewsDataNew?.data?.totalCount) || 0}
          />
        )
      )}
    </>
  )
}

ReviewsMobile.propTypes = {
  loadingReviews: PropTypes.bool.isRequired,

  reviewsDataNew: PropTypes.shape({
    data: PropTypes.shape({
      review: PropTypes.arrayOf(PropTypes.object),

      overall_rating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

      /** ✅ This fixes: reviewsDataNew.data.reviews */
      reviews: PropTypes.shape({
        '5_rating': PropTypes.number,
        '4_rating': PropTypes.number,
        '3_rating': PropTypes.number,
        '2_rating': PropTypes.number,
        '1_rating': PropTypes.number,
      }),

      average_per_category: PropTypes.object,
    }),
  }),

  reviewCategoriesMobile: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.any.isRequired,
    }),
  ).isRequired,

  platformDataMobile: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      icon: PropTypes.any,
      alt: PropTypes.string,
      label: PropTypes.string.isRequired,
      rating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      hasBorder: PropTypes.bool,
      hasSpecialLayout: PropTypes.bool,
      size: PropTypes.number,
    }),
  ).isRequired,

  skeletonData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      iconSize: PropTypes.number,
      labelWidth: PropTypes.number,
      hasBorder: PropTypes.bool,
      hasSpecialLayout: PropTypes.bool,
    }),
  ).isRequired,

  totalCount: PropTypes.number.isRequired,

  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}

export default ReviewsMobile
